import { FeConstants } from 'constants/FeConstants';
import { colors } from 'crono-fe-common/theme';
import { ImageResources } from 'crono-fe-common/types/enums/imageResources';
import { User } from 'crono-fe-common/types/user';
import { FileError } from 'use-file-picker/dist/interfaces';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import get from 'lodash/get';

export function randomIntegerValue(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function downloadCSVFile(
  filename: string,
  csv: string,
  contentType = 'text/csv',
) {
  const blob = new Blob([csv], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
}

export const isDev = () => {
  return (
    process.env.VITE_APP_ENVIRONMENT === 'dev' ||
    process.env.VITE_APP_ENVIRONMENT === 'local'
  );
};

/**
 * Connecting Dynamics switches the whole subscription onto
 * IntegrationType.Dynamics, so it stays out of the CRM pickers outside dev
 * until the production Entra app is publisher verified.
 */
export const isDynamicsIntegrationAvailable = () => isDev();

export const isLocal = () => {
  return process.env.VITE_APP_ENVIRONMENT === 'local';
};

export const parseFileError = (error: FileError & { message?: string }) => {
  let message = 'An error occurred while uploading the file';
  if (error.fileSizeToolarge) {
    message = 'The file is too large' + (error.name ? ': ' + error.name : '');
  } else if (error.imageWidthTooBig || error.imageHeightTooBig) {
    message = 'The image is too big' + (error.name ? ': ' + error.name : '');
  } else if (error.maxLimitExceeded) {
    message = `You can upload up to ${FeConstants.maxFilesPerTransaction} files`;
  } else if (error.message === FeConstants.maxLimitReachedMessage) {
    message = FeConstants.maxLimitReachedMessage;
  }
  return message;
};

export const filterNullProperties = <T extends object>(obj: T): Partial<T> => {
  return Object.keys(obj).reduce((acc, key) => {
    if (obj[key as keyof T] !== null) {
      acc[key as keyof T] = obj[key as keyof T];
    }
    return acc;
  }, {} as Partial<T>);
};

export const getExternalImage = (resource: ImageResources) => {
  return `${process.env.VITE_APP_S3_URL}/${resource}`;
};

export const generateRandomString = (length = 10) => {
  const characters = 'abcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const underlineInputStyles = (isError: boolean | undefined) => ({
  '& .MuiInput-underline:before': {
    borderBottomColor: isError ? colors.inactive : colors.grey444,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: isError ? colors.inactive : colors.primary,
  },
});

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64data = reader.result as string;
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const base64ToBlobAsync = async (
  base64: string,
  contentType = 'audio/wav',
): Promise<Blob> => {
  const response = await fetch(`data:${contentType};base64,${base64}`);
  return response.blob();
};

export const checkObjectIncludeValue = <T1 extends Record<string, unknown>, T2>(
  object: T1,
  value: T2,
) => {
  return Object.values(object).includes(value);
};

export const findKeyByValue = (
  object: Record<string, string>,
  value: string,
): string | undefined => {
  return Object.keys(object).find((key) => object[key] === value);
};

export const convertToPercentage = (total: number, valueToConvert: number) =>
  total === 0 ? 0 : Math.round((valueToConvert / total) * 1000) / 10;

export const generateChartGrids = (
  maxValue?: number,
  desiredTickCount = 5,
): number[] => {
  if (maxValue == null || isNaN(maxValue) || maxValue < 0) return [];
  if (desiredTickCount < 2) return [maxValue];

  if (maxValue <= desiredTickCount) {
    const grid: number[] = [];
    for (let i = 0; i <= maxValue; i++) {
      grid.push(i);
    }
    return grid;
  }

  const createGrid = (ticks: number) => {
    const rawStep = maxValue / (ticks - 1);
    const exponent = Math.floor(Math.log10(rawStep));
    const magnitude = Math.pow(10, exponent);
    const allowedMultipliers =
      magnitude === 1
        ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
        : [1, 2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10];
    const fraction = rawStep / magnitude;
    let chosenMultiplier = allowedMultipliers[0];
    for (const m of allowedMultipliers) {
      if (m <= fraction) {
        chosenMultiplier = m;
      } else {
        break;
      }
    }
    const step = chosenMultiplier * magnitude;
    const grid: number[] = [];
    for (let i = 0; i < ticks; i++) {
      grid.push(i * step);
    }
    const gridTop = grid[grid.length - 1];
    const error = maxValue - gridTop;
    return { grid, error, step };
  };

  const candidate1 = createGrid(desiredTickCount);
  let candidate2 = null;
  if (desiredTickCount > 2) {
    candidate2 = createGrid(desiredTickCount - 1);
  }

  let chosenGrid: number[];
  if (candidate2 && candidate2.error < candidate1.error) {
    chosenGrid = candidate2.grid;
  } else {
    chosenGrid = candidate1.grid;
  }

  return chosenGrid;
};

export const getColorFromArray = (colors: string[], index: number) =>
  colors[index % colors.length];

export const getMaxNumberOfAIVariables = (user?: User | null) => {
  return user?.otherSettings?.maxNumberOfAiVariables ?? 5;
};

export const splitEPsToSingleAndMultiLine = (
  externalProperties?: ExternalProperty[] | null,
): [
  singleLineExternalProperties: ExternalProperty[],
  multiLineExternalProperties: ExternalProperty[],
] => {
  const single: ExternalProperty[] = [];
  const multiple: ExternalProperty[] = [];
  (externalProperties || [])
    .filter((externalProperty) => !externalProperty.isTag)
    .forEach((ep) => {
      if (ep.aiVariableSettings || ep.valueType === 'Text') {
        multiple.push(ep);
      } else {
        single.push(ep);
      }
    });
  return [single, multiple];
};

export const makeHoursMultiline = (value: string) => {
  const [hour, period] = (value as string).split(' ');
  return `${hour}\n${period}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TIndexedObject<T = any> = Record<string, T>;

export const createHashedObject = <
  T extends TIndexedObject,
  Prop extends keyof T,
  ValueArray extends boolean = false,
>(
  array: T[] = [],
  prop: Prop = 'id' as Prop,
  isValueArray?: ValueArray,
  shouldRemoveKeyField?: boolean,
): Record<T[Prop], ValueArray extends true ? T[] : T> => {
  return array.reduce(
    (acc, current) => {
      const key = get(current, prop as string) as unknown as T[Prop];

      if (shouldRemoveKeyField) {
        delete current[prop];
      }

      if (isValueArray) {
        const existingValue = acc[key] as T[] | undefined;
        acc[key] = [
          ...(Array.isArray(existingValue) ? existingValue : []),
          current,
        ] as ValueArray extends true ? T[] : T;
      } else {
        acc[key] = current as ValueArray extends true ? T[] : T;
      }

      return acc;
    },
    {} as Record<T[Prop], ValueArray extends true ? T[] : T>,
  );
};

export const handleEmailInputChange = (
  newInputValue: string,
  currentEmails: string[],
  setEmails: (emails: string[]) => void,
  setQuery: (query: string) => void,
) => {
  const separatorRegex = /[;,]/;
  if (separatorRegex.test(newInputValue)) {
    const emails = newInputValue
      .split(separatorRegex)
      .map((email) => email.trim())
      .filter((email) => email.length && !currentEmails.includes(email));

    if (emails.length > 0) {
      setEmails([...currentEmails, ...emails]);
    }

    setQuery('');
  } else {
    setQuery(newInputValue);
  }
};

// Chart utilities for logarithmic scale
export const toLogScale = (value: number): number => {
  if (value <= 0) return 0;
  return Math.log10(value);
};

export const fromLogScale = (logValue: number): number => {
  return Math.pow(10, logValue);
};

// Format axis values with K/M/B abbreviations
export const formatChartAxisValue = (
  value: number,
  useLogScale = false,
): string => {
  if (!isFinite(value)) return '0';

  // If using log scale, convert back to original value for display
  const displayValue = useLogScale ? fromLogScale(value) : value;

  const isNegative = displayValue < 0;
  const absValue = Math.abs(displayValue);

  if (absValue >= 1000000000) {
    return `${isNegative ? '-' : ''}${Math.floor(absValue / 1000000000)}B`;
  }
  if (absValue >= 1000000) {
    return `${isNegative ? '-' : ''}${Math.floor(absValue / 1000000)}M`;
  }
  if (absValue >= 1000) {
    return `${isNegative ? '-' : ''}${Math.floor(absValue / 1000)}K`;
  }
  return Math.round(displayValue).toString();
};

// Generate nice tick values for chart axes
export const generateChartTicks = (
  min: number,
  max: number,
  targetTickCount = 6,
  isLogScale = false,
): number[] => {
  // Handle invalid inputs
  if (!isFinite(min) || !isFinite(max)) return [0];
  if (min === max) return [min];

  if (isLogScale && min > 0 && max > 0) {
    // Generate logarithmic ticks
    const logMin = Math.log10(min);
    const logMax = Math.log10(max);

    // Add padding in log space
    const paddedLogMax = logMax + (logMax - logMin) * 0.15;
    const paddedLogMin = Math.max(0, logMin - (logMax - logMin) * 0.15);

    const ticks: number[] = [];
    const logRange = paddedLogMax - paddedLogMin;

    if (logRange < 2) {
      // Small log range - use linear spacing in log space
      if (targetTickCount <= 1) {
        ticks.push(paddedLogMin);
      } else {
        const step = logRange / (targetTickCount - 1);
        for (let i = 0; i < targetTickCount; i++) {
          ticks.push(paddedLogMin + step * i);
        }
      }
    } else {
      // Large log range - use powers of 10
      const minPower = Math.floor(paddedLogMin);
      const maxPower = Math.ceil(paddedLogMax);

      for (let power = minPower; power <= maxPower; power++) {
        ticks.push(power);
      }
    }

    return ticks;
  }

  // Linear scale
  const paddedMax =
    max > 0 ? max * 1.15 : max === 0 ? Math.abs(min) * 0.15 || 1 : max * 0.85;
  const paddedMin =
    min < 0
      ? min * 1.15
      : min === 0
        ? -(Math.abs(max) * 0.15 || 1)
        : min * 0.85;

  const actualMin = Math.min(paddedMin, 0);
  const actualMax = Math.max(paddedMax, 0);

  const range = actualMax - actualMin;
  if (targetTickCount <= 1) return [paddedMin];
  const roughStep = range / (targetTickCount - 1);

  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalizedStep = roughStep / magnitude;

  let niceStep: number;
  if (normalizedStep <= 1) niceStep = 1;
  else if (normalizedStep <= 2) niceStep = 2;
  else if (normalizedStep <= 5) niceStep = 5;
  else niceStep = 10;

  niceStep *= magnitude;

  const niceMin = Math.floor(actualMin / niceStep) * niceStep;
  const niceMax = Math.ceil(actualMax / niceStep) * niceStep;

  const ticks: number[] = [];
  for (let tick = niceMin; tick <= niceMax; tick += niceStep) {
    const roundedTick = Math.round(tick / magnitude) * magnitude;
    ticks.push(roundedTick);
  }

  if (min < 0 && max > 0 && !ticks.includes(0)) {
    ticks.push(0);
    ticks.sort((a, b) => a - b);
  }

  return ticks.length > 0 ? ticks : [0];
};
